
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, X, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

interface CustomerSignatureManagerProps {
  customerName: string;
  receiptId: string;
}

const CustomerSignatureManager = ({ customerName, receiptId }: CustomerSignatureManagerProps) => {
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Load existing signature from localStorage on mount
  useState(() => {
    const savedSignature = localStorage.getItem(`customer_signature_${customerName}`);
    if (savedSignature) {
      setSignatureUrl(savedSignature);
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setSignatureFile(file);
    }
  };

  const handleUpload = async () => {
    if (!signatureFile) return;
    
    setIsUploading(true);
    
    try {
      // Convert file to base64 for localStorage storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        localStorage.setItem(`customer_signature_${customerName}`, base64String);
        setSignatureUrl(base64String);
        setSignatureFile(null);
        setIsUploading(false);
        
        // Reset file input
        const fileInput = document.getElementById('signature-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      };
      reader.readAsDataURL(signatureFile);
    } catch (error) {
      console.error('Error uploading signature:', error);
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    localStorage.removeItem(`customer_signature_${customerName}`);
    setSignatureUrl(null);
    setSignatureFile(null);
    
    // Reset file input
    const fileInput = document.getElementById('signature-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          Customer Signature/Logo
          <span className="text-sm font-normal text-gray-600">for {customerName}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!signatureUrl && (
          <div className="space-y-3">
            <Label htmlFor="signature-upload">Upload customer logo/signature</Label>
            <Input
              id="signature-upload"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="cursor-pointer"
            />
            {signatureFile && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Selected: {signatureFile.name}</span>
                <Button
                  size="sm"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            )}
          </div>
        )}
        
        {signatureUrl && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-green-600">
                ✓ Signature uploaded for {customerName}
              </span>
              <div className="flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <div className="text-center">
                      <h3 className="text-lg font-semibold mb-4">Customer Signature Preview</h3>
                      <img 
                        src={signatureUrl} 
                        alt="Customer signature"
                        className="max-w-full max-h-64 mx-auto border border-gray-200 rounded"
                      />
                    </div>
                  </DialogContent>
                </Dialog>
                <Button size="sm" variant="outline" onClick={handleRemove}>
                  <X className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded border-2 border-dashed border-gray-200">
              <img 
                src={signatureUrl} 
                alt="Customer signature preview"
                className="max-h-16 mx-auto"
              />
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500">
          This signature/logo will be included in printed receipts for this customer. 
          Supported formats: JPG, PNG, GIF. Max size: 5MB.
        </p>
      </CardContent>
    </Card>
  );
};

export default CustomerSignatureManager;
